// ABOUTME: DTO types of the bulk change (find & replace) REST API (/api/find-replace), mirrored from the server module.
// ABOUTME: Keep in sync with server/src/findReplace/types.ts: the UI cannot import server code.

import { SearchFilter } from '@src/__generated__/types';

export type ContentTypeRef = { ctg: string; ct: string };

/** The nodes a run works on: the list view's filters, optionally limited to a subtree. */
export type BulkChangeScope = {
    hierarchyId: string;
    /** Subtree root; absent means the whole hierarchy. */
    rootNodeId?: string;
    /** The filters the list view sends to searchHierarchies; empty means every node in range. */
    filters: SearchFilter[];
};

/** Whether an action writes over an existing value or only fills an empty one. */
export type WriteMode = 'overwrite' | 'onlyIfEmpty';

/** Whether an inherited value counts as empty (only own values count) or as filled. */
export type InheritedValues = 'empty' | 'filled';

type ActionBase = ContentTypeRef & {
    /** Empty or absent: every company language (only the default one for a non-localised field). */
    locales?: string[];
    /** Every existing group index of the ctg instead of index 0 only. */
    allGroupIndexes?: boolean;
};

export type ReplaceTextAction = ActionBase & {
    kind: 'replaceText';
    find: string;
    isRegex: boolean;
    caseSensitive: boolean;
    wholeWord: boolean;
    /** JS String.prototype.replace semantics: $1, $<name>, $&, $$. */
    replace: string;
};

export type SetValueAction = ActionBase & {
    kind: 'setValue';
    /** One stored value: a string, or `{ value, measure }` for a measure. */
    value: unknown;
    writeMode: WriteMode;
    inheritedValues: InheritedValues;
};

export type ClearValueAction = ActionBase & { kind: 'clearValue' };

export type CopyValueAction = ActionBase & {
    kind: 'copyValue';
    /** The source field; `lang` absent means the target language. */
    from: ContentTypeRef & { lang?: string };
    /** The only target language; absent means `locales`. */
    toLang?: string;
    writeMode: WriteMode;
    inheritedValues: InheritedValues;
};

export type LinksAction = ActionBase & { kind: 'addLinks' | 'removeLinks'; nodeIds: string[] };

export type ReplaceLinkAction = ActionBase & {
    kind: 'replaceLink';
    fromNodeId: string;
    toNodeId: string;
};

/**
 * Which own values of a target language a translation replaces: absent, none (only empty
 * languages are translated); `unapproved`, the machine translations not approved yet; `all`, every
 * value. A language maintained by hand (`translate: false`) is never replaced.
 */
export type TranslationOverwrite = 'unapproved' | 'all';

/** Machine-translates the default-language value list into `locales` (required here). */
export type TranslateAction = ActionBase & {
    kind: 'translate';
    locales: string[];
    overwrite?: TranslationOverwrite;
};

/**
 * Deletes every node in scope with its descendants; the only action of its run. `linkedAction`
 * says what happens to the nodes linking to a deleted one: `unlink` keeps them and drops the
 * dead references, `cascade` deletes them too.
 */
export type DeleteNodeAction = { kind: 'deleteNode'; linkedAction: 'unlink' | 'cascade' };

export type BulkChangeAction =
    | ReplaceTextAction
    | SetValueAction
    | ClearValueAction
    | CopyValueAction
    | LinksAction
    | ReplaceLinkAction
    | TranslateAction
    | DeleteNodeAction;

/** The actions that write a field. */
export type FieldAction = Exclude<BulkChangeAction, DeleteNodeAction>;

export type BulkChangeActionKind = BulkChangeAction['kind'];

export const ACTION_KINDS: BulkChangeActionKind[] = [
    'setValue',
    'clearValue',
    'copyValue',
    'replaceText',
    'addLinks',
    'removeLinks',
    'replaceLink',
    'translate',
    'deleteNode',
];

/** The field types each field action accepts. */
export const ACTION_TYPES: Record<FieldAction['kind'], string[]> = {
    replaceText: ['text', 'html'],
    setValue: ['text', 'html', 'select', 'number', 'boolean', 'measure', 'dateRange'],
    clearValue: [
        'text',
        'html',
        'select',
        'number',
        'boolean',
        'measure',
        'dateRange',
        'currency',
        'link',
        'media',
    ],
    copyValue: ['text', 'html', 'select', 'number', 'boolean', 'measure', 'dateRange'],
    addLinks: ['link', 'media'],
    removeLinks: ['link', 'media'],
    replaceLink: ['link', 'media'],
    translate: ['text', 'html'],
};

/** What a run (and a saved rule) does: a scope and the actions applied to it. */
export type RunInput = { scope: BulkChangeScope; actions: BulkChangeAction[] };

/** A run that deletes its nodes: its delete action is its only one. */
export const isDeleteRun = (input: Pick<RunInput, 'actions'>) =>
    input.actions[0]?.kind === 'deleteNode';

export type RunStatus =
    | 'scanning'
    | 'ready'
    | 'too_many_matches'
    | 'failed'
    | 'applying'
    | 'applied'
    | 'cancelled';

/** Statuses during which a background job is still working on the run. */
export const ACTIVE_RUN_STATUSES: RunStatus[] = ['scanning', 'applying'];

export type RowStatus = 'change' | 'skipped' | 'invalid';

export type SkipReason =
    | 'NO_WRITE_PERMISSION'
    | 'NODE_READONLY'
    | 'DEALERSHIP_READONLY'
    | 'FIELD_READONLY'
    | 'REGEX_TIMEOUT'
    /** The cascade of a delete removes more nodes than one delete may. */
    | 'CASCADE_TOO_LARGE'
    /** The delete guard refuses the delete: `skipDetail` holds its message. */
    | 'DELETE_REFUSED';

/** A language's whole value list; null when the slot has no own value. */
export type ValueList = unknown[] | null;

export type ApplyOutcome = 'applied' | 'conflict' | 'skipped';

export type ConflictReason = 'VALUE_CHANGED' | 'NOW_INHERITED' | 'MISSING';

/**
 * The `applyReason` of a delete row deleted, or not, through another row: with its pending
 * ancestor, by the cascade of an earlier root, or left because its ancestor's delete failed.
 */
export type DeleteOutcomeReason =
    | 'DELETED_WITH_ANCESTOR'
    | 'DELETED_BY_CASCADE'
    | 'ANCESTOR_NOT_DELETED';

export type DroppedContentTypeReason =
    | 'NOT_FOUND'
    | 'UNSUPPORTED_TYPE'
    | 'READ_ONLY'
    | 'CALCULATED'
    | 'ENCRYPTED'
    | 'NO_VIEW_ACCESS';

export type DroppedContentType = ContentTypeRef & { reason: DroppedContentTypeReason };

export type ScanInfo = {
    droppedContentTypes: DroppedContentType[];
    /** Inherited matches whose owner is above the starting node. */
    outOfScopeInherited: number;
    regexTimeouts: number;
    /** The objects in scope the scan looked at (absent until the scope is resolved). */
    scopeCount?: number;
    /** Set when the scan failed. */
    error?: string;
};

export type ApprovePublishFailure = {
    nodeId: string;
    nodeCode: string;
    ctg: string;
    message: string;
};

export type ApplyResult = {
    counts: Record<ApplyOutcome, number>;
    /** True when the apply stopped on a cancel: the counts cover what was already done. */
    cancelled: boolean;
    approvePublish?: {
        attempted: number;
        failures: ApprovePublishFailure[];
    };
};

/** A row as the REST API returns it. */
export type FindReplaceRowDto = {
    seq: number;
    nodeId: string;
    nodeCode: string;
    actionIndex: number;
    ctg: string;
    ctgIndex: number;
    ct: string;
    lang: string;
    valueIndex: number;
    /** Text of the value (replaceText), or a display string of the value list (other actions). */
    before: string | null;
    after: string | null;
    /** The language's whole value list before / after, for the actions other than replaceText. */
    beforeValue: ValueList;
    afterValue: ValueList;
    matchCount: number;
    inheritedCount: number;
    /** Delete rows of a cascade: the nodes outside the node's subtree its cascade also deletes. */
    linkedDeleteCount: number | null;
    status: RowStatus;
    skipReason: SkipReason | null;
    /** What the skip reason does not say: the refusal message of a `DELETE_REFUSED` row. */
    skipDetail: string | null;
    excluded: boolean;
    applyOutcome: ApplyOutcome | null;
    applyReason: string | null;
};

export type RunSummary = {
    byStatus: Record<RowStatus, number>;
    /** `change` rows the user excluded. */
    excluded: number;
    byContentType: (ContentTypeRef & { count: number })[];
    byLang: { lang: string; count: number }[];
    byOutcome: Record<ApplyOutcome, number>;
    byAction: { actionIndex: number; count: number }[];
    /** Skipped rows by reason. */
    bySkipReason: { reason: SkipReason; count: number }[];
};

/** The job currently working on a run (scan or apply); `state` is the job v2 state. */
export type RunJobDto = {
    id: string;
    state: string;
    /** 0–100. */
    progress: number;
    progressMessage: string | null;
};

/** GET /runs/:runId. */
export type FindReplaceRunDto = {
    id: string;
    hierarchyId: string;
    rootNodeId: string;
    status: RunStatus;
    input: RunInput;
    /** The saved rule the run was created from. */
    ruleId: string | null;
    createdBy: string;
    /** The active market the run was created in, passed to its jobs. */
    market: string | null;
    createdAt: string;
    expiresAt: string;
    reapprovePublish: boolean;
    scanInfo: ScanInfo | null;
    result: ApplyResult | null;
    summary: RunSummary;
    /** Distinct nodes of the `change` rows that are not excluded. */
    nodeCount: number;
    /** Distinct nodes with at least one applied row. */
    appliedNodeCount: number;
    /** The user holds the approveRoles of at least one group with `change` rows. */
    canReapprove: boolean;
    /** The scan or apply job of a `scanning` / `applying` run. */
    job: RunJobDto | null;
};

/** POST /runs: the new run and the scan job working on it. */
export type CreateRunResponse = { runId: string; jobId: string };

/** POST /runs/:runId/apply: the apply job. */
export type StartApplyResponse = { jobId: string };

export type RowsFilter = {
    status?: RowStatus;
    actionIndex?: number;
    ctg?: string;
    ct?: string;
    lang?: string;
    excluded?: boolean;
    outcome?: ApplyOutcome;
    /** Node code search (case-insensitive contains). */
    q?: string;
};

export type ExclusionsFilter = Pick<
    RowsFilter,
    'status' | 'actionIndex' | 'ctg' | 'ct' | 'lang' | 'q'
>;

/** GET /runs/:runId/rows. */
export type FindReplaceRowsResponse = {
    rows: FindReplaceRowDto[];
    count: number;
};

/** Server-side paging of GET /runs/:runId/rows: the server caps `limit` at 200. */
export type RowsPaging = {
    skip: number;
    limit: number;
};

/** PATCH /runs/:runId/exclusions: exactly one of `seqs` and `filter`. */
export type ExclusionsRequest =
    | { excluded: boolean; seqs: number[]; filter?: never }
    | { excluded: boolean; filter: ExclusionsFilter; seqs?: never };

export type LocalizedName = { languageIsoCode: string; value: string }[];

export type EligibleContentType = ContentTypeRef & {
    type: string;
    ctgName: LocalizedName;
    ctName: LocalizedName;
    repeatable: boolean;
    multiLanguage: boolean;
    multiValue: boolean;
    /** The hierarchy a link / media field points to. */
    hierarchy: string | null;
    /** The unit kind of a measure field (convert-units measure, e.g. "length"). */
    kind: string | null;
    /** The options of a select field. */
    options: { value: string; label: LocalizedName }[];
    /** Writable by at least one action (the others are only usable in the scope conditions). */
    editable: boolean;
};

/** GET /options. */
export type FindReplaceOptions = {
    /** Every content type the user can view. */
    contentTypes: EligibleContentType[];
    locales: string[];
    defaultLocale: string | null;
    /** The name of each company language, by ISO code. */
    localeNames: Record<string, string>;
    /** The languages the user may machine-translate into (translate action targets). */
    translatableLocales: string[];
    /** The name of the hierarchy's object schema. */
    objectSchemaName: LocalizedName;
    /** The object schema forbids deleting nodes (`disableDelete`): no delete action. */
    deleteDisabled: boolean;
};

/**
 * GET /runs/:runId/delete-impact: what applying a delete run removes, each object counted once.
 * `descendants` and `linked` exclude the selected objects and each other.
 */
export type DeleteImpactDto = {
    /** The selected objects. */
    objects: number;
    /** Their descendants that are not selected themselves. */
    descendants: number;
    /** The objects outside those subtrees that the cascades delete (0 when unlinking). */
    linked: number;
};

/** POST /scope-preview. */
export type ScopePreview = {
    count: number;
    /** Nodes in the hierarchy or branch with no filters. */
    totalInRange: number;
    tooLarge: boolean;
    nodes: {
        id: string;
        code: string;
        listName: string;
        /** Codes of the ancestors below the hierarchy root, top first. */
        breadcrumb: string[];
    }[];
};

export type RuleTriggers = {
    afterImport: boolean;
    /** Unattended runs (schedule, after import) apply at most this many changes on their own. */
    unattended: { maxChanges: number; reapprovePublish: boolean } | null;
};

/** A saved bulk change, as the REST API returns it. */
export type BulkChangeRuleDto = {
    id: string;
    name: string;
    description: string | null;
    enabled: boolean;
    definition: RunInput;
    triggers: RuleTriggers;
    /** The active market the rule was saved in. */
    market: string | null;
    owner: string;
    createdBy: string;
    createdAt: string;
    updatedBy: string;
    updatedAt: string;
    lastRunId: string | null;
};

export type RuleBody = Pick<BulkChangeRuleDto, 'name' | 'enabled' | 'definition' | 'triggers'> & {
    description?: string | null;
};

/** One run of a rule in its history. */
export type RuleRunSummary = {
    id: string;
    status: RunStatus;
    createdBy: string;
    createdAt: string;
    counts: Record<RowStatus, number>;
    result: ApplyResult | null;
};

/** The job type of a saved rule run: its schedules are ordinary job schedules of this type. */
export const BULK_CHANGE_RULE_JOB = 'bulk-change-rule';

/** Callbacks of the routes that start background jobs, provided by the dialog's host. */
export type FindReplaceJobCallbacks = {
    /** Starts a scan and resolves with the new run id. */
    onScan: (input: RunInput) => Promise<string>;
    /** Starts the apply and resolves with its job id. */
    onApply: (runId: string, reapprovePublish: boolean) => Promise<string | void>;
    onCancel: (runId: string) => Promise<void>;
};
